import { MaskType } from "@/core/types"
import { PhotoFlagStatus, PhotosViewState, Rating } from "@/lib/catalog"
import { ColorTag, CullingValues } from "@/utils/exif"

type CullingType = (typeof CullingValues)[number]

export const actionShortcuts = {
	toggleCrop: "r",
	toggleHealing: "q",
	toggleMask: "w",
	invertMask: "i",
	prevAdjustment: ",",
	nextAdjustment: ".",
	brushSwitchCursor: "alt",
	brushSizeMinus: "[",
	brushSizePlus: "]",
	openBasicEdit: "e",
	faceAutoExposure: "e",
	historyUndo: "mod+z",
	historyRedo: "mod+shift+z",
	markReference: "m",
	removeReference: "mod+m",
	lockPhoto: "l",
	unlockPhoto: "mod+l",
	hidePhoto: "h",
	unhidePhoto: "mod+h",
	showBefore: "\\",
	toggleHighlightShadowClipping: "j",
	zoom: "z",
	copyAdjustments: "mod+c",
	pasteAdjustments: "mod+v",
	selectAll: "mod+a",
	openPhotoSearch: "mod+f",
	togglePhotoFullScreen: "f",
	toggleFullScreen: "shift+f",

	subjectMask: "s",
	backgroundMask: "a",
	skyMask: "y",
	brushMask: "b",
	gradientMask: "o",
	radialMask: "k",

	gridView: "g",
	focusView: "d",
	compareView: "c",

	flagged: "p",
	unflagged: "u",
	rejected: "x",

	cullingCandidate: "c",
	cullingKeeper: "t",
	cullingReject: "r",
	cullingClear: "a",

	colorTagRed: "6",
	colorTagYellow: "7",
	colorTagGreen: "8",
	colorTagBlue: "9",

	rate0: "0",
	rate1: "1",
	rate2: "2",
	rate3: "3",
	rate4: "4",
	rate5: "5",

	escape: "esc",
	enter: "enter",
	space: "space",
	backspace: "backspace",
	del: "del",
	delete: "delete",
} as const

export const eventToActionKey = (event: KeyboardEvent): string => {
	const key = event.key.toLowerCase()
	const modifiers = [
		event.ctrlKey || event.metaKey ? "mod" : null,
		event.altKey ? "alt" : null,
		event.shiftKey ? "shift" : null,
	].filter(Boolean)
	const keyCombo = [...modifiers, key].join("+")
	return keyCombo
}

export const actionShortcutSet = new Set<string>(Object.values(actionShortcuts))

export const cullingKeyToValue = {
	[actionShortcuts.cullingCandidate]: "Candidate",
	[actionShortcuts.cullingKeeper]: "Keeper",
	[actionShortcuts.cullingReject]: "Reject",
} as const satisfies Record<string, CullingType>

export const colorKeyToValue = {
	[actionShortcuts.colorTagRed]: "Red",
	[actionShortcuts.colorTagYellow]: "Yellow",
	[actionShortcuts.colorTagGreen]: "Green",
	[actionShortcuts.colorTagBlue]: "Blue",
} as const satisfies Record<string, ColorTag>

export const flagKeyToValue = {
	[actionShortcuts.flagged]: "flagged",
	[actionShortcuts.unflagged]: "unflagged",
	[actionShortcuts.rejected]: "rejected",
} as const satisfies Record<string, PhotoFlagStatus>

export const ratingKeyToValue = {
	[actionShortcuts.rate0]: 0,
	[actionShortcuts.rate1]: 1,
	[actionShortcuts.rate2]: 2,
	[actionShortcuts.rate3]: 3,
	[actionShortcuts.rate4]: 4,
	[actionShortcuts.rate5]: 5,
} as const satisfies Record<string, Rating>

export const photosViewKeyToValue = {
	[actionShortcuts.gridView]: "grid",
	[actionShortcuts.focusView]: "focus",
	[actionShortcuts.compareView]: "before/after",
} as const satisfies Record<string, PhotosViewState>

export const maskTypeKeyToValue = {
	[actionShortcuts.subjectMask]: "subject",
	[actionShortcuts.backgroundMask]: "background",
	[actionShortcuts.skyMask]: "sky",
	[actionShortcuts.brushMask]: "brush",
	[actionShortcuts.gradientMask]: "gradient",
	[actionShortcuts.radialMask]: "radial",
} as const satisfies Record<string, MaskType>
